// A tab notices a new deploy and reloads itself (Umer, 2026-09-21).
//
// WHY. A browser keeps the JavaScript it loaded until the page is reloaded.
// After the 2026-09-21 board fix went live, dispatch tabs opened the day
// before kept running the OLD email sync, and it wiped 565 live cards in four
// sweeps the next morning. A deploy fixes only the tabs that reload.
//
// HOW. The build bakes its own id into the bundle (`__BUILD_ID__`, from
// vite.config.ts) and emits the same id at /version.json. Every minute, and
// whenever the tab comes back to the foreground, this asks for /version.json
// (never cached) and compares. When the ids differ the tab reloads at the
// next quiet moment — hidden, or no dialog open and nothing being typed — and
// unconditionally after FORCE_AFTER_MS, so a tab left on a screen overnight
// cannot outlive a deploy by more than ten minutes.
//
// Spread (Phase 1, 2026-09-30): hidden tabs were quiet at once, so every
// background tab in the office reloaded within the same minute of a deploy —
// sixty-odd fresh sockets, channel joins and full first loads at once. A hidden
// tab now waits its own random share of HIDDEN_SPREAD_MS first; a tab that is
// shown meanwhile reloads at once (quiet rules), and the ten-minute cap holds.
//
// Loop guard: the id we reloaded for is remembered per tab session, so a CDN
// still serving the previous index.html for a moment cannot make the tab
// reload forever — it waits for the next different id.

declare const __BUILD_ID__: string | undefined;

/** The id of the build this code came from; 'dev' outside a Vite build. */
export const BUILD_ID: string = typeof __BUILD_ID__ === 'string' && __BUILD_ID__ ? __BUILD_ID__ : 'dev';

export const VERSION_URL = '/version.json';
export const POLL_MS = 60_000;          // steady-state check
export const RETRY_MS = 15_000;         // re-check while a reload is pending
export const FORCE_AFTER_MS = 10 * 60_000;
export const HIDDEN_SPREAD_MS = 8 * 60_000; // hidden tabs reload somewhere in their first 8 minutes
export const RELOADED_KEY = 'wd:reloaded-for-build';

export type ReloadDecision = 'none' | 'wait' | 'reload';

/** Pure policy: given what the server says and the tab's state, what now? */
export function decideReload(input: {
  current: string;
  remote: string | null;          // null = could not read version.json
  staleSince: number | null;      // when we first saw a different id
  now: number;
  quiet: boolean;                 // safe to reload right now
  alreadyReloadedFor: string | null;
  hidden?: boolean;               // nobody is looking at the tab
  hiddenDelayMs?: number;         // this tab's share of HIDDEN_SPREAD_MS
}): ReloadDecision {
  const { current, remote, staleSince, now, quiet, alreadyReloadedFor } = input;
  if (!remote || remote === current) return 'none';
  if (alreadyReloadedFor === remote) return 'none';
  const since = staleSince ?? now;
  if (now - since >= FORCE_AFTER_MS) return 'reload';
  if (input.hidden && now - since < (input.hiddenDelayMs ?? 0)) return 'wait';
  return quiet ? 'reload' : 'wait';
}

/** A moment when reloading loses nothing: the tab is hidden, or no dialog is
 *  open and the person is not typing into anything. */
export function isQuietMoment(doc: Document): boolean {
  if (doc.visibilityState === 'hidden') return true;
  if (doc.querySelector('[role="dialog"], [role="alertdialog"]')) return false;
  const el = doc.activeElement as HTMLElement | null;
  if (!el) return true;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return false;
  return !el.isContentEditable;
}

export interface BuildWatchOptions {
  current?: string;
  url?: string;
  fetchFn?: (input: string, init?: RequestInit) => Promise<Response>;
  doc?: Document;
  win?: Window;
  reload?: () => void;
  now?: () => number;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  /** This tab's hidden-reload delay; random in [0, HIDDEN_SPREAD_MS) by default. */
  hiddenDelayMs?: number;
}

/** Start watching for a new build. Returns a stop function. */
export function startBuildWatch(opts: BuildWatchOptions = {}): () => void {
  const current = opts.current ?? BUILD_ID;
  const url = opts.url ?? VERSION_URL;
  const doc = opts.doc ?? (typeof document !== 'undefined' ? document : undefined);
  const win = opts.win ?? (typeof window !== 'undefined' ? window : undefined);
  const fetchFn = opts.fetchFn ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined);
  const now = opts.now ?? (() => Date.now());
  const reload = opts.reload ?? (() => win?.location.reload());
  const storage = opts.storage === undefined
    ? (() => { try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; } })()
    : opts.storage;
  if (!doc || !win || !fetchFn || current === 'dev') return () => {};
  const hiddenDelayMs = opts.hiddenDelayMs ?? Math.random() * HIDDEN_SPREAD_MS;

  let staleSince: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let inFlight = false;

  const readStored = (): string | null => { try { return storage?.getItem(RELOADED_KEY) ?? null; } catch { return null; } };
  const remember = (id: string): void => { try { storage?.setItem(RELOADED_KEY, id); } catch { /* private mode */ } };

  const schedule = (ms: number) => {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { void check(); }, ms);
  };

  const check = async (): Promise<void> => {
    if (stopped || inFlight) return;
    inFlight = true;
    let remote: string | null = null;
    try {
      const res = await fetchFn(`${url}?t=${now()}`, { cache: 'no-store', credentials: 'same-origin' });
      if (res.ok) {
        const body = (await res.json()) as { build?: unknown };
        remote = typeof body?.build === 'string' && body.build ? body.build : null;
      }
    } catch { remote = null; }
    inFlight = false;
    if (stopped) return;

    const t = now();
    const decision = decideReload({
      current, remote, staleSince, now: t, quiet: isQuietMoment(doc), alreadyReloadedFor: readStored(),
      hidden: doc.visibilityState === 'hidden', hiddenDelayMs,
    });
    if (decision === 'none') { staleSince = null; schedule(POLL_MS); return; }
    if (staleSince === null) staleSince = t;
    if (decision === 'wait') { schedule(RETRY_MS); return; }
    remember(remote as string);
    stopped = true;
    reload();
  };

  const onVisible = () => { if (doc.visibilityState === 'visible') void check(); };
  const onFocus = () => { void check(); };
  doc.addEventListener('visibilitychange', onVisible);
  win.addEventListener('focus', onFocus);
  schedule(POLL_MS);

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    doc.removeEventListener('visibilitychange', onVisible);
    win.removeEventListener('focus', onFocus);
  };
}
